import React, { useEffect, useRef, useState } from "react";
import "./AudioPlayer.scss";

const AudioPlayer = ({ src }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(100);

    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const volumeAdjustRef = useRef(null);
    const volumeLevelRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const progressBar = progressRef.current;
        const volumeAdjust = volumeAdjustRef.current;
        const volumeLevel = volumeLevelRef.current;

        const updateProgress = () => {
            if (!isNaN(audio.duration)) {
                setCurrentTime(audio.currentTime);
                const progressPercent = (audio.currentTime / audio.duration) * 100;
                progressBar.style.width = `${progressPercent}%`;
            }
        };

        const setAudioDuration = () => {
            if (!isNaN(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        const handleProgressClick = (event) => {
            const rect = progressBar.parentNode.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const newTime = (clickX / rect.width) * audio.duration;
            if (!isNaN(newTime)) {
                audio.currentTime = newTime;
            }
        };

        const handleVolumeAdjustMouseDown = (event) => {
            const adjustVolume = (event) => {
                const rect = volumeAdjust.getBoundingClientRect();
                const y = event.clientY - rect.top;
                const newVolume = Math.min(100, Math.max(0, 100 - (y / rect.height) * 100));
                setVolume(newVolume);
                audio.volume = newVolume / 100;
                volumeLevel.style.height = `${newVolume}%`;
            };

            const handleMouseMove = (event) => {
                adjustVolume(event);
            };

            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            adjustVolume(event);

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };

        audio.volume = volume / 100;

        audio.addEventListener('loadedmetadata', setAudioDuration);
        audio.addEventListener('timeupdate', updateProgress);
        progressBar.parentNode.addEventListener('click', handleProgressClick);
        volumeAdjust.addEventListener('mousedown', handleVolumeAdjustMouseDown);

        audio.addEventListener('error', (e) => {
            console.error("Error occurred while playing audio", e);
        });

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioDuration);
            audio.removeEventListener('timeupdate', updateProgress);
            progressBar.parentNode.removeEventListener('click', handleProgressClick);
            volumeAdjust.removeEventListener('mousedown', handleVolumeAdjustMouseDown);
        };
    }, [volume]);

    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(error => {
                console.error("Error attempting to play audio:", error);
            });
        }
        setIsPlaying(!isPlaying);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    return (
        <div id="audio-toggler-23323" className="collapse in" style={{ height: 'auto' }}>
            <div className={`audioplayer ${isPlaying ? 'audioplayer-playing' : 'audioplayer-stopped'}`}>
                <audio id="audio" ref={audioRef} src={src} preload="auto"></audio>
                <div className="audioplayer-controls">
                    <div className="audioplayer-playpause" onClick={handlePlayPause}>
                        <button id="playPauseButton">
                            {isPlaying ? 'Pause' : 'Play'}
                        </button>
                    </div>
                </div>
                <div className="audioplayer-progress-wrapper">
                    <div id="time" className="audioplayer-time">{formatTime(currentTime)}</div>
                    <div id="progress" className="audioplayer-progress">
                        <div id="progressBar" className="audioplayer-progress-bar" ref={progressRef}></div>
                    </div>
                    <div id="duration" className="audioplayer-time">{formatTime(duration)}</div>
                </div>
                <div className="audioplayer-volume">
                    <div className="audioplayer-volume-button" title="Volume">
                        <a href="/" onClick={(event) => event.preventDefault()}>
                            Volume
                        </a>
                    </div>
                    <div id="volumeAdjust" className="audioplayer-volume-adjust" ref={volumeAdjustRef}>
                        <div id="volumeLevel" style={{ height: `${volume}%` }} ref={volumeLevelRef}></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;